<script setup lang="ts">
import { computed } from 'vue'
import { NConfigProvider, NMessageProvider, darkTheme, dateZhCN, zhCN } from 'naive-ui'
import { themeOverridesFor } from '../theme'
import { isDarkTone, type Tone } from '../tokens'
import { useToneVars } from '../tone-vars'
import NavIcon from './NavIcon.vue'

/**
 * 登录页版式:左侧品牌区 + 右侧居中的卡片。
 *
 * ═══ S-41 按画板重排(O8) ═══
 *
 * 品牌区固定 520 宽、通高渐变(与侧栏同一块底,`--sd-brand-panel`),内容是
 * 一只品牌方块的纸飞机 + 端名 + 一行副标题,底端收着可选的要点清单
 * (`points`:每行一枚品牌蓝对勾 + 一行字)。旧版的航迹线稿 SVG 撤掉了 ——
 * 两块画板(运营端 O8 与监管端 G4)上都量不到它,版式是替换不是叠加。
 *
 * **要点清单是可选的**(不传则品牌区只有标题 + 副标题):监管端的登录页还没重排
 * (G4 在后面的切片),它的现状必须原样能跑 —— 加一个 prop,不动它。
 *
 * 为什么左栏两端都是深色(运营端的内容区已经是深色了,这一栏也照样是那条更深的渐变):
 * 登录页是这套系统给人的第一眼,深色版画是唯一一处不用讲道理、只负责「像样」的地方。
 *
 * 窄屏(<900px)直接砍掉左栏:把它压窄只会得到一条挤满字的窄条,
 * 不如干脆让卡片占满。这是规格 §4.3 写明的行为。
 *
 * 与 AppShell 一样内含 provider(且同样走 isDarkTone 选 naive 的深色主题)——
 * 登录页也用到 `useMessage`。
 */
const props = withDefaults(
  defineProps<{
    tone?: Tone
    product: string
    tagline: string
    /** 品牌区底端的要点清单(O8 的三条)。不传就只有标题 + 副标题 */
    points?: readonly string[]
  }>(),
  { tone: 'light' },
)

const vars = useToneVars(() => props.tone)

const dark = computed(() => isDarkTone(props.tone))
</script>

<template>
  <n-config-provider
    :theme="dark ? darkTheme : null"
    :theme-overrides="themeOverridesFor(props.tone)"
    :locale="zhCN"
    :date-locale="dateZhCN"
  >
    <n-message-provider>
      <div class="login" :style="vars">
        <section class="brand-panel">
          <div class="brand-head">
            <span class="brand-mark"><NavIcon name="plane" :size="26" /></span>
            <span class="brand-name">{{ props.product }}</span>
            <p class="tagline">{{ props.tagline }}</p>
          </div>

          <ul v-if="props.points && props.points.length > 0" class="points">
            <li v-for="(point, index) in props.points" :key="index" class="point">
              <NavIcon class="point-icon" name="check" :size="14" />
              <span class="point-text">{{ point }}</span>
            </li>
          </ul>
        </section>

        <section class="form-panel">
          <div class="card">
            <slot />
          </div>
        </section>
      </div>
    </n-message-provider>
  </n-config-provider>
</template>

<style scoped>
.login {
  display: flex;
  min-height: 100vh;
  background: var(--sd-bg);
  font-family: var(--sd-font-sans);
}

/* 520 固定宽、上组贴顶下组贴底(画板:上组是品牌三行,下组是要点清单) */
.brand-panel {
  flex: 0 0 520px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 32px;
  padding: 40px;
  overflow: hidden;
  background: var(--sd-brand-panel);
  color: var(--sd-sidebar-text-active);
}

.brand-head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
}

.brand-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: var(--sd-radius-md);
  background: var(--sd-brand-mark);
  color: var(--sd-sidebar-text-active);
}

.brand-name {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.tagline {
  margin: 0;
  font-size: 14px;
  color: var(--sd-sidebar-text);
}

.points {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.point {
  display: flex;
  align-items: center;
  gap: 8px;
}

.point-icon {
  color: var(--sd-brand-mark);
}

.point-text {
  font-size: 13px;
  color: var(--sd-sidebar-text);
}

.form-panel {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
}

/* 登录卡:底 + 一圈描边,没有投影(画板的卡片语言是色块与描边,不是浮起来) */
.card {
  width: 400px;
  max-width: 100%;
  padding: 32px;
  border-radius: var(--sd-radius-xl);
  background: var(--sd-surface);
  border: 1px solid var(--sd-border);
  color: var(--sd-text1);
}

@media (max-width: 899px) {
  .brand-panel {
    display: none;
  }

  .login {
    background: var(--sd-bg);
  }
}
</style>
